/*
 * COPYRIGHT © 2026 SANITÀNEXUS WHITE-LABEL. ALL RIGHTS RESERVED.
 * AUTHOR AND SOLE DEVELOPER: MARCO PANZETTI (C.F. PNZMRC89M14F251C)
 * Protected under Italian Copyright Law (Legge 633/1941) and International Treaties.
 * Unauthorized copying, reverse engineering, or distribution of this software architecture,
 * UI/UX layouts, local-first logic, or system prompt configurations is strictly prohibited.
 */
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import api from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Calendar, Plus, MapPin, Clock, Trash2, Bell, GraduationCap, CalendarClock } from 'lucide-react';
import { toast } from 'sonner';

const SHIFT_META = {
  shift: { color: 'var(--wl-primary)', Icon: CalendarClock, label: 'shiftTypeShift' },
  reminder: { color: '#7C3AED', Icon: Bell, label: 'shiftTypeReminder' },
  course: { color: '#DB2777', Icon: GraduationCap, label: 'shiftTypeCourse' },
  event: { color: '#1D4ED8', Icon: Calendar, label: 'shiftTypeEvent' },
};

const fmtDate = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, { weekday: 'short', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch { return iso; }
};

const ShiftDialog = ({ open, onOpenChange, initial, onSaved }) => {
  const { t } = useTranslation();
  const isEdit = !!initial?.id;
  const [form, setForm] = useState({
    title: '',
    shift_type: 'shift',
    start_at: '',
    end_at: '',
    location: '',
    notes: '',
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setForm({
        title: initial?.title || '',
        shift_type: initial?.shift_type || 'shift',
        start_at: (initial?.start_at || '').slice(0, 16),
        end_at: (initial?.end_at || '').slice(0, 16),
        location: initial?.location || '',
        notes: initial?.notes || '',
      });
    }
  }, [open, initial]);

  const submit = async () => {
    if (!form.title.trim() || !form.start_at) {
      toast.error(t('shiftMissingFields'));
      return;
    }
    setSaving(true);
    try {
      const payload = {
        title: form.title.trim(),
        shift_type: form.shift_type,
        start_at: new Date(form.start_at).toISOString(),
        end_at: form.end_at ? new Date(form.end_at).toISOString() : null,
        location: form.location.trim() || null,
        notes: form.notes.trim() || null,
      };
      const r = isEdit
        ? await api.put(`/shifts/${initial.id}`, payload)
        : await api.post('/shifts', payload);
      toast.success(isEdit ? t('shiftUpdated') : t('shiftCreated'));
      onSaved?.(r.data);
      onOpenChange(false);
    } catch {
      toast.error(t('errorOccurred'));
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{isEdit ? t('editShift') : t('newShift')}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>{t('shiftTitle')}</Label>
            <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder={t('shiftTitlePlaceholder')} data-testid="shift-title-input" />
          </div>
          <div>
            <Label>{t('shiftType')}</Label>
            <Select value={form.shift_type} onValueChange={(v) => setForm({ ...form, shift_type: v })}>
              <SelectTrigger data-testid="shift-type-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.entries(SHIFT_META).map(([k, m]) => (
                  <SelectItem key={k} value={k} data-testid={`shift-type-option-${k}`}>
                    <span className="inline-flex items-center gap-1.5"><m.Icon className="h-3.5 w-3.5" style={{ color: m.color }} />{t(m.label)}</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label>{t('startAt')}</Label>
              <Input type="datetime-local" value={form.start_at} onChange={(e) => setForm({ ...form, start_at: e.target.value })} data-testid="shift-start-input" />
            </div>
            <div>
              <Label>{t('endAt')}</Label>
              <Input type="datetime-local" value={form.end_at} onChange={(e) => setForm({ ...form, end_at: e.target.value })} data-testid="shift-end-input" />
            </div>
          </div>
          <div>
            <Label>{t('locationOptional')}</Label>
            <Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} placeholder={t('locationPlaceholder')} data-testid="shift-location-input" />
          </div>
          <div>
            <Label>{t('notesOptional')}</Label>
            <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="shift-notes-input" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={saving}>{t('cancel')}</Button>
          <Button onClick={submit} disabled={saving} data-testid="shift-save-button">{isEdit ? t('save') : t('create')}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const ShiftsPage = () => {
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openNew, setOpenNew] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    try {
      setLoading(true);
      const r = await api.get('/shifts');
      setItems(r.data || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    if (!window.confirm(t('confirmDelete'))) return;
    try {
      await api.delete(`/shifts/${id}`);
      setItems(prev => prev.filter(x => x.id !== id));
      toast.success(t('shiftDeleted'));
    } catch { toast.error(t('errorOccurred')); }
  };

  const onSaved = (updated) => {
    setItems(prev => {
      const idx = prev.findIndex(x => x.id === updated.id);
      if (idx >= 0) { const copy = [...prev]; copy[idx] = updated; return copy; }
      return [...prev, updated].sort((a, b) => (a.start_at || '').localeCompare(b.start_at || ''));
    });
  };

  const now = new Date().toISOString();
  const upcoming = items.filter(x => (x.end_at || x.start_at) >= now);
  const past = items.filter(x => (x.end_at || x.start_at) < now);

  return (
    <div className="space-y-4 max-w-3xl mx-auto pb-8" data-testid="shifts-page">
      <div className="flex items-center gap-3">
        <div className="h-10 w-10 rounded-xl bg-primary/10 text-primary flex items-center justify-center">
          <Calendar className="h-5 w-5" />
        </div>
        <div className="flex-1 min-w-0">
          <h1 className="font-display font-semibold text-2xl leading-tight tracking-tight">{t('shiftsTitle')}</h1>
          <p className="text-xs text-muted-foreground">{t('shiftsSubtitle')}</p>
        </div>
        <Button onClick={() => { setEditing(null); setOpenNew(true); }} data-testid="shift-new-button" className="gap-1.5">
          <Plus className="h-4 w-4" /> {t('newShift')}
        </Button>
      </div>

      <Section title={t('upcoming')} count={upcoming.length}>
        {upcoming.length === 0 && !loading && (
          <EmptyState title={t('shiftsEmptyTitle')} subtitle={t('shiftsEmptySub')} onCreate={() => { setEditing(null); setOpenNew(true); }} createLabel={t('newShift')} />
        )}
        {upcoming.map(s => (
          <ShiftRow key={s.id} s={s} onEdit={() => { setEditing(s); setOpenNew(true); }} onDelete={() => remove(s.id)} t={t} />
        ))}
      </Section>

      {past.length > 0 && (
        <Section title={t('past')} count={past.length}>
          {past.slice(0, 20).map(s => (
            <ShiftRow key={s.id} s={s} onEdit={() => { setEditing(s); setOpenNew(true); }} onDelete={() => remove(s.id)} t={t} muted />
          ))}
        </Section>
      )}

      <ShiftDialog open={openNew} onOpenChange={setOpenNew} initial={editing} onSaved={onSaved} />
    </div>
  );
};

const Section = ({ title, count, children }) => (
  <div>
    <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2 flex items-center gap-1.5">
      {title}
      <span className="text-[10px] bg-secondary/60 rounded-full px-1.5 py-0.5">{count}</span>
    </div>
    <div className="space-y-2">{children}</div>
  </div>
);

const ShiftRow = ({ s, onEdit, onDelete, t, muted }) => {
  const meta = SHIFT_META[s.shift_type] || SHIFT_META.shift;
  return (
    <Card
      className={`p-3 rounded-2xl cursor-pointer hover:shadow-sm transition-all ${muted ? 'opacity-60' : ''}`}
      onClick={onEdit}
      data-testid={`shift-item-${s.id}`}
    >
      <div className="flex items-start gap-3">
        <div className="h-9 w-9 rounded-xl flex items-center justify-center flex-shrink-0"
             style={{ backgroundColor: `${meta.color}20`, color: meta.color }}>
          <meta.Icon className="h-4 w-4" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-sm truncate">{s.title}</div>
          <div className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5">
            <Clock className="h-3 w-3 flex-shrink-0" /> {fmtDate(s.start_at)}
            {s.end_at && <> — {fmtDate(s.end_at)}</>}
          </div>
          {s.location && (
            <div className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5">
              <MapPin className="h-3 w-3 flex-shrink-0" /> {s.location}
            </div>
          )}
          {s.notes && <div className="text-xs text-muted-foreground mt-1 line-clamp-2">{s.notes}</div>}
        </div>
        <Button
          variant="ghost" size="icon"
          onClick={(e) => { e.stopPropagation(); onDelete(); }}
          className="h-8 w-8 text-muted-foreground hover:text-destructive"
          data-testid={`shift-delete-${s.id}`}
        >
          <Trash2 className="h-3.5 w-3.5" />
        </Button>
      </div>
    </Card>
  );
};

const EmptyState = ({ title, subtitle, onCreate, createLabel }) => (
  <div className="text-center py-10 opacity-70">
    <div className="mx-auto h-12 w-12 rounded-2xl bg-secondary/60 flex items-center justify-center mb-3">
      <Calendar className="h-5 w-5" />
    </div>
    <div className="font-semibold text-sm">{title}</div>
    <div className="text-xs text-muted-foreground mt-1 mb-4">{subtitle}</div>
    <Button size="sm" onClick={onCreate} className="gap-1.5"><Plus className="h-4 w-4" />{createLabel}</Button>
  </div>
);

export default ShiftsPage;
